// File: src/services/api.js
// Purpose: Centralized API service for all backend calls with security
// Last modified: 2025-11-16 - Added request signature security

import SecurityUtils from '../utils/security';
import {
    storeEncryptionKey,
    clearStoredKey,
    hasEncryptionKey,
    decryptPayload,
} from '../utils/payloadEncryption';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://www.zakovat-savollari.com';

let _keyInitPromise = null;

async function initEncryptionKey() {
    if (_keyInitPromise) return _keyInitPromise;
    _keyInitPromise = (async () => {
        try {
            const { key } = await fetchWrapper(`${API_BASE_URL}/api/session/encryption-key`);
            const raw = Uint8Array.from(atob(key), c => c.charCodeAt(0));
            const cryptoKey = await crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['decrypt']);
            storeEncryptionKey(cryptoKey);
        } catch {
            _keyInitPromise = null;
        }
    })();
    return _keyInitPromise;
}

export function clearEncryptionKey() {
    clearStoredKey();
    _keyInitPromise = null;
}

export { initEncryptionKey };

/**
 * Generic fetch wrapper with security headers and error handling
 */
async function fetchWrapper(url, options = {}) {
    const securityHeaders = SecurityUtils.getSecurityHeaders(
        options.method || 'GET',
        url
    );

    const config = {
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'X-Supports-Payload-Encryption': 'true',
            ...securityHeaders,
            ...options.headers,
        },
        ...options,
    };

    let response;
    try {
        response = await fetch(url, config);
    } catch (networkError) {
        console.error(`Network Error [${url}]:`, networkError);
        throw networkError;
    }

    if (!response.ok) {
        let errorCode = null;
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
            const body = await response.json();
            if (body.error) errorCode = body.error;
            if (body.message) errorMessage = body.message;
        } catch {}

        const error = new Error(errorMessage);
        error.status = response.status;
        error.errorCode = errorCode;
        console.error(`API Error [${url}]:`, error);
        throw error;
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
        return await response.json();
    }
    return await response.text();
}

/**
 * API Service Object
 */
const apiService = {
    // ==================== Authentication ====================
    auth: {
        /**
         * Get current user information
         * @returns {Promise<Object>} User object
         */
        getCurrentUser: () =>
            fetchWrapper(`${API_BASE_URL}/api/user`),

        /**
         * Request login token via email
         * @param {string} email - User's email address
         * @returns {Promise<Object>}
         */
        requestLoginToken: (email) =>
            fetchWrapper(`${API_BASE_URL}/request-login-token`, {
                method: 'POST',
                body: JSON.stringify({ email }),
            }),

        /**
         * Logout current user
         * @returns {Promise<boolean>}
         */
        logout: () =>
            fetchWrapper(`${API_BASE_URL}/logout`, {
                method: 'GET',
            }),

        /**
         * Get OAuth2 login URLs
         */
        getOAuthUrls: () => ({
            google: `${API_BASE_URL}/api/login/google`,
        }),
    },

    // ==================== Packages ====================
    packages: {
        /**
         * Get all packages
         * @returns {Promise<Array>} Array of package objects
         */
        getAll: () =>
            fetchWrapper(`${API_BASE_URL}/api/packages/`),

        /**
         * Get single package with questions
         * @param {string} packageId - Package ID
         * @returns {Promise<Object>} Package object with questions
         */
        getById: async (packageId) => {
            if (!hasEncryptionKey()) await initEncryptionKey();
            const encrypted = await fetchWrapper(`${API_BASE_URL}/api/package/${packageId}`);
            try {
                return await decryptPayload(encrypted);
            } catch (decryptError) {
                // Key is stale (e.g. the session ID rotated after login) — refresh and retry once
                console.warn('Decryption failed, refreshing key and retrying:', decryptError);
                clearEncryptionKey();
                await initEncryptionKey();
                return await decryptPayload(encrypted);
            }
        },

        /**
         * Get PDF download URL
         * @param {string} packageId - Package ID
         * @param {boolean} separatePage - Show answers on separate page
         * @returns {string} Download URL
         */
        getPdfDownloadUrl: (packageId, separatePage = false) =>
            `${API_BASE_URL}/toplamlar/${packageId}/download?showAnswerInSeparatePage=${separatePage}`,
    },

    // ==================== Saved Questions ====================
    savedQuestions: {
        /**
         * Get all saved questions for current user
         * @returns {Promise<Array>} Array of saved question objects
         */
        getAll: () =>
            fetchWrapper(`${API_BASE_URL}/api/savedQuestion/all`),

        /**
         * Save a question
         * @param {string} questionId - Question ID
         * @returns {Promise<Object>}
         */
        save: (questionId) =>
            fetchWrapper(`${API_BASE_URL}/api/savedQuestion/${questionId}`, {
                method: 'POST',
            }),

        /**
         * Delete a saved question
         * @param {string} questionId - Question ID
         * @returns {Promise<Object>}
         */
        delete: (questionId) =>
            fetchWrapper(`${API_BASE_URL}/api/savedQuestion/delete/${questionId}`, {
                method: 'POST',
            }),
    },

    // ==================== Admin ====================
    admin: {
        /**
         * Search user by email (admin only)
         * @param {string} email - User's email
         * @returns {Promise<Object>} User object
         */
        searchUserByEmail: (email) =>
            fetchWrapper(
                `${API_BASE_URL}/api/users/search-by-email?email=${encodeURIComponent(email)}`
            ),

        /**
         * Get admin user edit URL
         * @param {string} userId - User ID
         * @returns {string} Admin edit URL
         */
        getUserEditUrl: (userId) =>
            `${API_BASE_URL}/admin/users/${userId}`,
    },

    // ==================== User Settings ====================
    userSettings: {
        get: () =>
            fetchWrapper(`${API_BASE_URL}/api/user/settings`),
        update: (patch) =>
            fetchWrapper(`${API_BASE_URL}/api/user/settings`, {
                method: 'PATCH',
                body: JSON.stringify(patch),
            }),
    },

    // ==================== Question Reports ====================
    questionReports: {
        submit: (questionId, comment) =>
            fetchWrapper(`${API_BASE_URL}/api/questionReport/${questionId}`, {
                method: 'POST',
                body: JSON.stringify({ comment }),
            }),
    },

    // ==================== Search ====================
    search: {
        /**
         * Search questions
         * @param {string} searchTerm - Search term
         * @returns {Promise<Array>} Array of question objects
         */
        searchQuestions: (searchTerm) =>
            fetchWrapper(`${API_BASE_URL}/api/search?searchTerm=${encodeURIComponent(searchTerm)}`),
    },
};

export default apiService;

// Export individual services for convenience
export const { auth, packages, savedQuestions, admin, search } = apiService;
